import { MessageSquareWarning, Star, Sparkles, CheckCircle2, HelpCircle, ShieldCheck, Search } from "lucide-react";

const CORROBORATION = {
  CORROBORATED: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Corroborated by the scan" },
  UNVERIFIED: { cls: "bg-slate-100 text-slate-500 border-slate-200", label: "Not independently verified — kept as a hypothesis" },
};

function Chip({ label, cls }) {
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>{label}</span>;
}

function ConcernRow({ c, idx }) {
  const corr = CORROBORATION[c.corroboration] || CORROBORATION.UNVERIFIED;
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={`owner-concern-${idx}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {c.is_priority && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
            <Star className="w-3 h-3 fill-amber-500" /> #1 concern
          </span>
        )}
        <span className="font-semibold text-slate-900">{c.label}</span>
        <Chip label="Owner-reported" cls="bg-indigo-50 text-indigo-700 border-indigo-200" />
        <Chip label={c.corroboration} cls={corr.cls} />
      </div>
      <p className="mt-1.5 text-[12px] text-slate-500">{corr.label}.{c.note ? ` ${c.note}` : ""}</p>
      {(c.corroborating_evidence || []).length > 0 && (
        <div className="mt-2 space-y-1">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">What the scan found</p>
          {c.corroborating_evidence.map((e, i) => (
            <p key={i} className="text-[12px] text-slate-700 flex items-start gap-1.5">
              <Search className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" /> {e.problem}
            </p>
          ))}
        </div>
      )}
      {!c.has_direct_service && (
        <p className="mt-2 text-[11px] text-slate-400">No direct marketing service — best addressed through the financial/consulting analysis.</p>
      )}
    </div>
  );
}

export function OwnerConcerns({ data }) {
  if (!data || !data.enabled) return null;
  const ai = data.ai_interpretation;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="owner-concerns">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 rounded-xl bg-amber-500 text-white"><MessageSquareWarning className="w-5 h-5" /></span>
        <div>
          <h3 className="font-heading text-xl font-bold text-slate-900">What's holding the business back</h3>
          <p className="text-[12px] text-slate-500">Owner-reported concerns, cross-referenced with the scan's evidence. v{data.model_version}</p>
        </div>
      </div>

      {(data.details || data.goals) && (
        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="owner-concerns-words">
          {data.details && (
            <>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">In the owner's words</p>
              <p className="text-sm text-slate-700 italic">"{data.details}"</p>
            </>
          )}
          {data.goals && (
            <p className="mt-2 text-[13px] text-slate-600"><span className="font-semibold">Desired outcome:</span> {data.goals}</p>
          )}
          <div className="mt-2"><Chip label="Owner-reported" cls="bg-indigo-50 text-indigo-700 border-indigo-200" /></div>
        </div>
      )}

      {(data.concerns || []).length > 0 && (
        <div className="mt-4 space-y-3">
          {data.concerns.map((c, i) => <ConcernRow key={i} c={c} idx={i} />)}
        </div>
      )}

      {ai && (ai.summary || (ai.interpretations || []).length) ? (
        <div className="mt-5 rounded-xl bg-violet-50/60 border border-violet-100 p-4" data-testid="owner-concerns-ai">
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles className="w-4 h-4 text-violet-600" />
            <span className="text-[11px] font-semibold text-violet-700 uppercase tracking-wide">AI-assisted interpretation</span>
          </div>
          {ai.summary && <p className="text-sm text-slate-700">{ai.summary}</p>}
          {(ai.interpretations || []).map((it, i) => (
            <div key={i} className="mt-3">
              <p className="text-[13px] font-semibold text-slate-800">{it.concern}</p>
              {(it.likely_drivers || []).map((d, j) => (
                <p key={`d${j}`} className="text-[12px] text-slate-600 flex items-start gap-1.5"><HelpCircle className="w-3.5 h-3.5 text-violet-400 shrink-0 mt-0.5" /> Possible cause: {d}</p>
              ))}
              {(it.what_to_check || []).map((w, j) => (
                <p key={`w${j}`} className="text-[12px] text-slate-600 flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" /> Check: {w}</p>
              ))}
            </div>
          ))}
          <p className="mt-3 text-[11px] text-violet-700/70">AI interpretation of what the owner described — hypotheses to investigate, not verified facts.</p>
        </div>
      ) : null}

      <div className="mt-5 flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3">
        <ShieldCheck className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
        <p className="text-[11px] text-slate-500">{data.disclaimer}</p>
      </div>
    </div>
  );
}

export default OwnerConcerns;
